import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Row,
  Skeleton,
  Steps,
  Tabs,
  Tag,
  Typography
} from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import PageHeader from "../shared/PageHeader";
import { formatDate } from "../shared/money";
import { Success, Error as ErrorMsg } from "../../../../commons/Messages/Messages";
import { errorText, getSettings, updateSettings } from "./api";
import { SECTIONS, WIZARD_SECTIONS, formatNumberPreview } from "./constants";
import {
  AdvisorSection,
  BookkeepingSection,
  CompanySection,
  DocumentsSection,
  DunningSection,
  InvoicesSection
} from "./sections";

const DATE_FIELDS = ["validFrom", "validUntil"];

// Settings → form values: ISO date strings become dayjs, nested objects are cloned so the
// form never mutates the loaded snapshot.
const toForm = settings => {
  const values = JSON.parse(JSON.stringify(settings));
  values.freistellungsbescheinigung = values.freistellungsbescheinigung || {};
  DATE_FIELDS.forEach(f => {
    const v = values.freistellungsbescheinigung[f];
    values.freistellungsbescheinigung[f] = v ? dayjs(v) : null;
  });
  values.dunning = {
    ...values.dunning,
    fees: [0, 1, 2, 3].map(i => values.dunning?.fees?.[i] ?? 0)
  };
  return values;
};

// Form values → PUT body for one section only.
const toBody = (values, section, settings) => {
  const body = {};
  section.keys.forEach(key => {
    if (values[key] !== undefined) body[key] = values[key];
  });
  if (body.freistellungsbescheinigung) {
    body.freistellungsbescheinigung = { ...body.freistellungsbescheinigung };
    DATE_FIELDS.forEach(f => {
      const v = body.freistellungsbescheinigung[f];
      body.freistellungsbescheinigung[f] = v ? dayjs(v).format("YYYY-MM-DD") : "";
    });
    delete body.freistellungsbescheinigung.document;
  }
  // The server re-seeds the sequence whenever this key is present; only send a real change.
  if (
    "invoiceNumberStart" in body &&
    Number(body.invoiceNumberStart) === Number(settings.invoiceNumberStart)
  ) {
    delete body.invoiceNumberStart;
  }
  return body;
};

const SectionForm = ({ section, settings, meta, onSaved, footer }) => {
  const { t } = useTranslation("accounting");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const chartLocked = (meta.sequences?.journal || 0) > 0;

  useEffect(() => {
    form.setFieldsValue(toForm(settings));
  }, [settings, form]);

  const save = async ({ finish = false } = {}) => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return false;
    }
    setSaving(true);
    try {
      const body = toBody(values, section, settings);
      if (finish) body.setupCompleted = true;
      const res = await updateSettings(body);
      Success(finish ? t("Setup completed") : t("Settings saved"));
      onSaved(res.data.settings);
      return true;
    } catch (err) {
      ErrorMsg(errorText(err, t("Could not save the settings")));
      if (err?.response?.status === 409) {
        // The server refused a protected change (chart / method / number start): show its values again.
        form.setFieldsValue(toForm(settings));
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const body = {
    company: <CompanySection />,
    bookkeeping: <BookkeepingSection chartLocked={chartLocked} charts={meta.meta?.charts} />,
    invoices: <InvoicesSection sequences={meta.sequences} />,
    dunning: <DunningSection />,
    advisor: <AdvisorSection />,
    documents: <DocumentsSection />
  }[section.key];

  return (
    <Form form={form} layout="vertical" initialValues={toForm(settings)}>
      {body}
      {footer({ save, saving })}
    </Form>
  );
};

const StatusCard = ({ settings, meta }) => {
  const { t } = useTranslation("accounting");
  const year = new Date().getFullYear();
  return (
    <Card title={t("Status")} size="small">
      {/* Full width below the form up to lg, a narrow side card from lg on. */}
      <Descriptions column={{ xs: 1, sm: 2, lg: 1 }} size="small">
        <Descriptions.Item label={t("Setup")}>
          {settings.setupCompletedAt ? (
            <Tag icon={<CheckCircleOutlined />} color="success">
              {t("Completed on {{date}}", {
                date: formatDate(dayjs(settings.setupCompletedAt).format("YYYY-MM-DD"))
              })}
            </Tag>
          ) : (
            <Tag color="warning">{t("Not completed")}</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t("Chart of accounts (Kontenrahmen)")}>
          {meta.chart?.seeded ? (
            <span>
              {settings.kontenrahmen?.toUpperCase()} ·{" "}
              {t("{{count}} accounts", { count: meta.chart.accounts })}
            </span>
          ) : (
            <Tag>{t("Not seeded yet")}</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t("Accounting method")}>
          {settings.accountingMethod === "bilanz"
            ? t("Bilanz (accrual basis)")
            : t("EÜR (cash basis)")}
        </Descriptions.Item>
        <Descriptions.Item label={t("Last invoice number {{year}}", { year })}>
          {meta.sequences?.invoice
            ? formatNumberPreview(settings.invoiceNumberFormat, meta.sequences.invoice)
            : t("none yet")}
        </Descriptions.Item>
        <Descriptions.Item label={t("Journal entries {{year}}", { year })}>
          {meta.sequences?.journal || 0}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

const Settings = () => {
  const { t } = useTranslation("accounting");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [tab, setTab] = useState(SECTIONS[0].key);

  const load = async () => {
    try {
      const res = await getSettings();
      if (!res.data?.settings) throw new Error(t("Could not load accounting settings"));
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(errorText(err, t("Could not load accounting settings")));
    }
  };

  useEffect(() => {
    load();
  }, []);

  // A saved section replaces the settings; sequences and chart status are refreshed too.
  const handleSaved = settings => {
    setData(prev => ({ ...prev, settings }));
    load();
  };

  const wizard = useMemo(() => Boolean(data && !data.settings.setupCompletedAt), [data]);

  if (error)
    return (
      <Alert
        type="error"
        showIcon
        message={error}
        action={<Button onClick={load}>{t("Retry")}</Button>}
      />
    );
  if (!data) return <Skeleton active paragraph={{ rows: 8 }} />;

  const { settings } = data;

  const wizardFooter =
    index =>
    ({ save, saving }) => {
      const last = index === WIZARD_SECTIONS.length - 1;
      return (
        <div className="acct-actions" style={{ marginBlockStart: 8 }}>
          <Button disabled={index === 0 || saving} onClick={() => setStep(index - 1)}>
            {t("Back")}
          </Button>
          <Button
            type="primary"
            loading={saving}
            onClick={async () => {
              const ok = await save({ finish: last });
              if (ok && !last) setStep(index + 1);
            }}
          >
            {last ? t("Save & finish setup") : t("Save & continue")}
          </Button>
        </div>
      );
    };

  const tabFooter = ({ save, saving }) => (
    <Button type="primary" loading={saving} onClick={() => save()} style={{ marginBlockStart: 8 }}>
      {t("Save")}
    </Button>
  );

  return (
    <div>
      <PageHeader
        title={t("Accounting settings")}
        subtitle={
          wizard
            ? t("Five short steps set up your bookkeeping. Every step is saved on its own.")
            : null
        }
      />
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={17}>
          {wizard ? (
            <Card>
              <Steps
                current={step}
                onChange={setStep}
                size="small"
                style={{ marginBottom: 24 }}
                items={WIZARD_SECTIONS.map(s => ({ key: s.key, title: t(s.title) }))}
              />
              <Typography.Title level={5}>{t(WIZARD_SECTIONS[step].title)}</Typography.Title>
              <SectionForm
                key={WIZARD_SECTIONS[step].key}
                section={WIZARD_SECTIONS[step]}
                settings={settings}
                meta={data}
                onSaved={handleSaved}
                footer={wizardFooter(step)}
              />
            </Card>
          ) : (
            <Card>
              <Tabs
                activeKey={tab}
                onChange={setTab}
                items={SECTIONS.map(s => ({
                  key: s.key,
                  label: t(s.title),
                  children: (
                    <SectionForm
                      section={s}
                      settings={settings}
                      meta={data}
                      onSaved={handleSaved}
                      footer={tabFooter}
                    />
                  )
                }))}
              />
            </Card>
          )}
        </Col>
        <Col xs={24} lg={7}>
          <StatusCard settings={settings} meta={data} />
        </Col>
      </Row>
    </div>
  );
};

export default Settings;
